import PropTypes from 'prop-types';
import { useContext } from 'react';
import { AppContext } from '../../context/Provider';
import PreloadImage from '../PreloadImage';
import SensorModels from '../SensorModels';
import { LABELS } from '../../config/data';
import { SET_SELECTED_TAB } from '../../context/actions';
import { sendUserInteractionsToTelemetryFor } from '../../utility/analytics.telemetry';
import { TAB_CLICK_EVENT } from '../../config/analytics.telemetry';
import {
	EVALKITS_TAB,
	MODEL_VARIANT_EVALKITS,
	MODEL_VARIANT_SAMPLES,
	SAMPLES_TAB,
	SCHEMATIC_TAB,
} from '../../config/tabbed-content';
import './style.css';

/**
 * Renders content specific to a selected tab of a selected sensor solution
 * @param {*} props Props passed to this component
 * @returns {React.ReactComponentElement} Tabbed Content Component
 */
const TabbedContent = props => {
	const classes = ['adi-isp__tabbed-content'];
	const { solution } = props;
	const { globalState, dispatch } = useContext(AppContext);
	const { selectedTab } = globalState;
	const { schematic, evalKits, samples, solutionId } = solution;
	const { indicators, imgUrl } = schematic;
	const { schematicTab, evaluationKitsTab, samplesTab } = LABELS;
	const isSchematicTabSelected = selectedTab === SCHEMATIC_TAB;
	const isEvalKitsTabSelected = selectedTab === EVALKITS_TAB;
	const isSamplesTabSelected = selectedTab === SAMPLES_TAB;

	/**
	 * Returns a function that updates the 'selectedTab' global state and makes a telemetry
	 * request for 'tab_click' event
	 * @param {string} selection The selected tab
	 * @returns {function} Function referencing the passed 'selection'
	 */
	const setSelectedTabTo = selection => () => {
		const isOtherTabSelected = selection !== selectedTab;

		if (isOtherTabSelected) {
			dispatch({ type: SET_SELECTED_TAB, payload: selection });
			sendUserInteractionsToTelemetryFor(TAB_CLICK_EVENT, { selection }, solutionId);
		}
	};

	return (
		<section className={classes.join(' ')}>
			<nav className={`${classes[0]}-tabs`}>
				<li
					className={`${classes[0]}-tab${
						isSchematicTabSelected ? ` ${classes[0]}-tab--selected` : ''
					}`}
					onClick={setSelectedTabTo(SCHEMATIC_TAB)}
				>
					<span>{schematicTab}</span>
				</li>

				<li
					className={`${classes[0]}-tab${
						isEvalKitsTabSelected ? ` ${classes[0]}-tab--selected` : ''
					}`}
					onClick={setSelectedTabTo(EVALKITS_TAB)}
				>
					<span>{evaluationKitsTab}</span>
				</li>

				<li
					className={`${classes[0]}-tab${
						isSamplesTabSelected ? ` ${classes[0]}-tab--selected` : ''
					}`}
					onClick={setSelectedTabTo(SAMPLES_TAB)}
				>
					<span>{samplesTab}</span>
				</li>
			</nav>

			<div className={`${classes[0]}-wrapper`}>
				{isSchematicTabSelected ? (
					<section className={`${classes[0]}-schematic`}>
						<div className={`${classes[0]}-schematic-image`}>
							<PreloadImage src={imgUrl} alt="Sensor Schematic Image" />
						</div>

						<ul className={`${classes[0]}-schematic-indicators`}>
							{indicators.map((indicator, index) => (
								<li
									key={`${indicator.type}-${index}`}
									className={`${classes[0]}-schematic-indicator`}
								>
									<span>{indicator.type}</span>
									<span>{indicator.value}</span>
								</li>
							))}
						</ul>
					</section>
				) : isEvalKitsTabSelected ? (
					<SensorModels
						variant={MODEL_VARIANT_EVALKITS}
						models={evalKits}
						solutionId={solutionId}
					/>
				) : (
					<SensorModels
						variant={MODEL_VARIANT_SAMPLES}
						models={samples}
						solutionId={solutionId}
					/>
				)}
			</div>
		</section>
	);
};

TabbedContent.propTypes = {
	solution: PropTypes.shape({
		schematic: PropTypes.shape({
			indicators: PropTypes.arrayOf(
				PropTypes.exact({
					type: PropTypes.string.isRequired,
					value: PropTypes.string.isRequired,
				}),
			),
			imgUrl: PropTypes.string.isRequired,
		}).isRequired,
		evalKits: PropTypes.arrayOf(
			PropTypes.exact({
				model: PropTypes.string.isRequired,
				description: PropTypes.string.isRequired,
				detailedInfoUrl: PropTypes.string.isRequired,
			}),
		).isRequired,
		samples: PropTypes.arrayOf(
			PropTypes.exact({
				partName: PropTypes.string.isRequired,
				description: PropTypes.string.isRequired,
				detailedInfoUrl: PropTypes.string.isRequired,
			}),
		).isRequired,
		solutionId: PropTypes.string.isRequired,
	}).isRequired,
};

export default TabbedContent;
